/** @jsxImportSource ./simboluri-jsx */
// SECȚIUNEA «Plan de armare» A PAGINII ELEMENTULUI: blocul propriu al
// elementului de pe coala lui (grindă: elevația axului cu deschiderea
// evidențiată, secțiunile și extrasul desenat; stâlp / lamelă: detaliul
// clasei și elevația stivei; ax de fundare: planul pe ax și secțiunea; ochi:
// planul plăcii cu ochiul aprins), desenat de server din rezultatele pașilor
// de armare (`planse/set.py::desen_element`).
//
// Desenul e și o hartă: clic pe altă grindă din elevație → pagina ei; clic pe
// o marcă («3g», «e1») → pagina barei. Butoanele duc la coala întreagă în
// Studio, descarcă blocul elementului (DXF / DWG) și pun desenul pe tot
// ecranul. Un element fără desen (pasul lui
// n-a rulat, sau familia n-are încă planșă) nu arată nimic — nici un card
// gol, nici o eroare.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t as tm, useT } from "../i18n";
import { arata } from "../toasts";
import { ApiError } from "../api";
import MeniuLista from "../nav/MeniuLista";
import {
  descarcaPlanArmare, descarcaPlanCofraj, descarcaPlanSapatura, fetchDrawingUrl,
  getPlanArmareColi,
  planArmareElementUrl, type PlanArmareColi, type TintaDescarcare,
} from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import { hashElement, hashMarca, hashPlanArmare } from "./legaturi";
import { esteMarca } from "./marci";
import type { DrawingData } from "./tipuri";

type DesenElement = DrawingData & { coala?: string | null };

/** Setul de planșe din care se descarcă: planul de armare, cel de cofraj sau cel
 *  de săpătură. */
export type SetPlanse = "plan_armare" | "plan_cofraj" | "plan_sapatura";

/** Descarcă fișierul colii și spune ce a ieșit: 409 → motivul; entități
 *  pierdute la conversia DWG → avertisment. Folosit și de vederile din
 *  Studio (planul de armare și planul de cofraj). */
export async function descarcaCuRaport(
  projectId: string, cloneId: string | null | undefined,
  fel: "dxf" | "dwg", tinta: TintaDescarcare, set: SetPlanse = "plan_armare",
): Promise<void> {
  try {
    const coala = typeof tinta === "string" ? tinta : tinta.element;
    const { raport } = set === "plan_cofraj"
      ? await descarcaPlanCofraj(projectId, cloneId, fel, coala)
      : set === "plan_sapatura"
        ? await descarcaPlanSapatura(projectId, cloneId, fel, coala)
        : await descarcaPlanArmare(projectId, cloneId, fel, tinta);
    if (raport && raport.pierdute > 0) {
      arata(tm("struct.pa.dwg_pierdute", { n: raport.pierdute, mesaj: raport.mesaj }), "info");
    }
  } catch (e) {
    const err = e as ApiError;
    const raport = (err?.date as { raport?: { mesaj?: string } } | undefined)?.raport;
    arata(raport?.mesaj || err?.message || tm("struct.pa.descarcare_esuata"));
  }
}

/** Colțurile de «ecran complet» — patru paranteze; întoarse spre interior
 *  când desenul e deja pe tot ecranul. */
function IconEcranComplet({ plin }: { plin: boolean }) {
  const d = plin
    ? "M5,1 L5,5 L1,5 M11,1 L11,5 L15,5 M15,11 L11,11 L11,15 M1,11 L5,11 L5,15"
    : "M1,5 L1,1 L5,1 M11,1 L15,1 L15,5 M15,11 L15,15 L11,15 M5,15 L1,15 L1,11";
  return (
    <svg viewBox="0 0 16 16" width={13} height={13} aria-hidden>
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.6}
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PlanArmareElement({ projectId, cloneId, refElement, solutie }: {
  projectId: string;
  cloneId?: string | null;
  refElement: string;
  solutie?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [coli, setColi] = useState<PlanArmareColi | null>(null);
  // `undefined` = pe drum; `null` = elementul n-are desen (404)
  const [desen, setDesen] = useState<DesenElement | null | undefined>(undefined);
  const [ocupat, setOcupat] = useState<"dxf" | "dwg" | null>(null);
  const [meniu, setMeniu] = useState(false);
  // ECRAN COMPLET: cardul întreg (bara cu descărcarea + desenul) intră pe tot
  // ecranul; Escape iese, iar starea se citește din document, nu se presupune
  const card = useRef<HTMLDivElement>(null);
  const [plin, setPlin] = useState(false);
  useEffect(() => {
    const laSchimbare = () => setPlin(
      !!card.current && document.fullscreenElement === card.current);
    document.addEventListener("fullscreenchange", laSchimbare);
    return () => document.removeEventListener("fullscreenchange", laSchimbare);
  }, []);

  useEffect(() => {
    if (cloneId == null) return;
    let viu = true;
    getPlanArmareColi(projectId, cloneId)
      .then((c) => { if (viu) setColi(c); })
      .catch(() => { if (viu) setColi(null); });
    return () => { viu = false; };
  }, [projectId, cloneId]);

  const versiune = coli?.versiune ?? null;
  const src = cloneId != null ? planArmareElementUrl(projectId, cloneId, refElement) : null;
  useEffect(() => {
    if (!src) return;
    let viu = true;
    setDesen(undefined);
    fetchDrawingUrl(src, versiune)
      .then((d) => { if (viu) setDesen(d as DesenElement); })
      .catch(() => { if (viu) setDesen(null); });
    return () => { viu = false; };
  }, [src, versiune]);

  if (!src || !desen) return null;

  const idCoala = desen.coala ?? null;
  const dwgOk = coli?.dwg_disponibil !== false;

  const laClic = (id: string | null) => {
    if (!id || id === refElement) return;
    if (esteMarca(id)) navigate(hashMarca(projectId, id, solutie));
    else navigate(hashElement(projectId, id, solutie));
  };

  // se descarcă BLOCUL elementului — exact desenul de pe pagină, nu coala
  const descarca = async (fel: "dxf" | "dwg") => {
    setMeniu(false);
    if (ocupat) return;
    setOcupat(fel);
    try {
      await descarcaCuRaport(projectId, cloneId, fel, { element: refElement });
    } finally {
      setOcupat(null);
    }
  };

  const comutaEcranul = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void card.current?.requestFullscreen?.();
  };

  return (
    <div ref={card} className="ob-card"
         style={plin
           ? { overflow: "hidden", display: "flex", flexDirection: "column",
               height: "100vh", borderRadius: 0, background: "var(--srf)" }
           : { overflow: "hidden" }}
         data-testid="plan-armare-element">
      <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b>{t("struct.pa.el_titlu")}</b>
        <span style={{ flex: 1 }} />
        {idCoala && (
          <a className="ob-chip ob-c-acc" style={{ textDecoration: "none" }}
             href={hashPlanArmare(projectId, solutie, idCoala)}
             title={t("struct.pa.deschide_titlu")}>
            {t("struct.pa.deschide")}
          </a>
        )}
        <span style={{ position: "relative", display: "inline-flex" }}>
          <button type="button" className="ob-chip ob-c-mut" disabled={ocupat != null}
                  aria-haspopup="menu" aria-expanded={meniu}
                  title={t("struct.pa.descarca_titlu", { ref: refElement })}
                  onClick={() => setMeniu(!meniu)}>
            {ocupat ? t("struct.pa.se_descarca") : t("struct.pa.descarca")} ▾
          </button>
          {meniu && (
            <MeniuLista laInchide={() => setMeniu(false)}>
              <button type="button" role="menuitem" className="meniu-rand"
                      title={t("struct.pa.dxf_element_titlu")}
                      onClick={() => { void descarca("dxf"); }}>
                {t("struct.pa.fel_dxf")}
              </button>
              <button type="button" role="menuitem" className="meniu-rand"
                      disabled={!dwgOk}
                      title={dwgOk ? t("struct.pa.dwg_element_titlu")
                        : t("struct.pa.dwg_indisponibil")}
                      onClick={() => { void descarca("dwg"); }}>
                {t("struct.pa.fel_dwg")}
              </button>
            </MeniuLista>
          )}
        </span>
        <button type="button" className="ob-chip ob-c-mut"
                style={{ display: "inline-flex", alignItems: "center", padding: "3px 7px" }}
                aria-label={t(plin ? "struct.pa.iesi_ecran_complet" : "struct.pa.ecran_complet")}
                title={t(plin ? "struct.pa.iesi_ecran_complet" : "struct.pa.ecran_complet")}
                onClick={comutaEcranul}>
          <IconEcranComplet plin={plin} />
        </button>
      </div>
      <div style={plin
             ? { flex: 1, minHeight: 0, borderTop: "1px solid var(--line)" }
             : { height: 520, borderTop: "1px solid var(--line)" }}>
        <DrawingView
          src={src}
          versiune={versiune}
          title={t("struct.pa.el_desen", { ref: refElement })}
          embed
          faraAntet
          faraBusola
          onSelect={(id) => laClic(id)}
        />
      </div>
    </div>
  );
}
